import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Calendar, Search, Filter, MapPin, Upload } from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { festivalsAPI, handleApiError } from '../utils/api';
import { formatEventPeriod } from '../utils/eventUtils';

const DEFAULT_COLOR = 'bg-blue-600';

const ManageFestivalsPage = () => {
  const navigate = useNavigate();
  const { userRole } = useAuth();

  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterParticipating, setFilterParticipating] = useState('all'); // all, participating, not-participating
  const [deletingId, setDeletingId] = useState(null);

  const loadFestivals = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await festivalsAPI.getAll();
      setEvents(response.data || []);
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      setError(`${errTitle} : ${details}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFestivals();
  }, [loadFestivals]);

  // Vérification admin
  if (userRole !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h2>
            <p className="text-gray-600 mb-6">Vous devez être administrateur pour accéder à cette page.</p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleDelete = async (event) => {
    if (!window.confirm(`Supprimer le festival "${event.title}" et tous ses tournois ? Cette action est irréversible.`)) {
      return;
    }
    setDeletingId(event.id);
    try {
      await festivalsAPI.delete(event.id);
      setEvents((prev) => prev.filter((e) => e.id !== event.id));
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      alert(`Impossible de supprimer : ${errTitle} - ${details}`);
    } finally {
      setDeletingId(null);
    }
  };

  const getColor = (event) => event.circuit?.color || DEFAULT_COLOR;

  // Filtrer les événements
  const filteredEvents = events.filter(event => {
    const matchSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       (event.description || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchParticipating = filterParticipating === 'all' ||
                              (filterParticipating === 'participating' && event.participating) ||
                              (filterParticipating === 'not-participating' && !event.participating);

    return matchSearch && matchParticipating;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center">
                  <Calendar className="mr-3" size={32} />
                  Gestion des Festivals
                </h1>
                <p className="text-gray-600 mt-2">Créez et gérez vos festivals de poker</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => navigate('/manage-festivals/import')}
                  className="bg-purple-100 text-purple-800 px-6 py-3 rounded-lg font-semibold hover:bg-purple-200 transition flex items-center justify-center"
                >
                  <Upload className="mr-2" size={20} />
                  Importer
                </button>
                <button
                  onClick={() => navigate('/manage-festivals/create')}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition flex items-center justify-center"
                >
                  <Plus className="mr-2" size={20} />
                  Nouveau Festival
                </button>
              </div>
            </div>

            {/* Filtres et recherche */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Barre de recherche */}
              <div className="relative">
                <Search className="absolute left-3 top-3 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Rechercher un festival..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Filtre participation */}
              <div className="relative">
                <Filter className="absolute left-3 top-3 text-gray-400" size={20} />
                <select
                  value={filterParticipating}
                  onChange={(e) => setFilterParticipating(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
                >
                  <option value="all">Tous les festivals</option>
                  <option value="participating">Participation confirmée</option>
                  <option value="not-participating">Non inscrit</option>
                </select>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              Chargement des festivals...
            </div>
          ) : (
            <>
              {/* Statistiques */}
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 text-sm">Total Festivals</p>
                      <p className="text-2xl font-bold text-gray-800">{events.length}</p>
                    </div>
                    <Calendar className="text-blue-600" size={32} />
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 text-sm">Participations</p>
                      <p className="text-2xl font-bold text-green-600">
                        {events.filter(e => e.participating).length}
                      </p>
                    </div>
                    <div className="bg-green-100 p-2 rounded-full">
                      <span className="text-2xl">✓</span>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 text-sm">Total Tournois</p>
                      <p className="text-2xl font-bold text-purple-600">
                        {events.reduce((acc, event) => acc + (event.subEvents?.length || 0), 0)}
                      </p>
                    </div>
                    <div className="bg-purple-100 p-2 rounded-full">
                      <span className="text-2xl">🎰</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Liste des festivals */}
              <div className="space-y-4">
                {filteredEvents.length === 0 ? (
                  <div className="bg-white rounded-lg shadow p-8 text-center">
                    <p className="text-gray-500">Aucun festival trouvé</p>
                  </div>
                ) : (
                  filteredEvents
                    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
                    .map(event => {
                      const color = getColor(event);
                      const subEvents = event.subEvents || [];
                      return (
                        <div
                          key={event.id}
                          className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition"
                        >
                          <div className={`${color} bg-opacity-20 border-l-8 ${color.replace('bg-', 'border-')} p-6`}>
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                              <div className="flex items-center gap-4 flex-1 min-w-0">
                                {event.circuit?.logoUrl && (
                                  <img
                                    src={event.circuit.logoUrl}
                                    alt="Logo"
                                    className="w-16 h-16 object-contain bg-white rounded-lg p-2"
                                  />
                                )}
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 mb-2">
                                    <h3 className="text-2xl font-bold text-gray-800">{event.title}</h3>
                                    {event.circuit?.acronym && (
                                      <span className="bg-gray-200 text-gray-700 text-xs font-semibold px-2 py-1 rounded">
                                        {event.circuit.acronym}
                                      </span>
                                    )}
                                    {event.participating && (
                                      <span className="bg-green-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                                        ✓ Inscrit
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-gray-600 mb-1">{formatEventPeriod(event)}</p>
                                  {event.venue?.name && (
                                    <p className="text-sm text-gray-600 flex items-center gap-1 mb-1">
                                      <MapPin size={14} />
                                      {event.venue.name}{event.venue.city ? ` — ${event.venue.city}` : ''}
                                    </p>
                                  )}
                                  <p className="text-sm text-gray-500">{event.description}</p>
                                  <div className="mt-3 flex items-center gap-4 text-sm">
                                    <span className={`${color} text-white px-3 py-1 rounded-full font-semibold`}>
                                      {subEvents.length} tournoi{subEvents.length > 1 ? 's' : ''}
                                    </span>
                                    <span className="text-gray-600">
                                      {subEvents.filter(se => se.participating).length} participation{subEvents.filter(se => se.participating).length > 1 ? 's' : ''}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Actions */}
                              <div className="flex gap-2 sm:ml-4 flex-shrink-0">
                                <button
                                  onClick={() => navigate(`/manage-festivals/edit/${event.id}`)}
                                  className="bg-blue-600 text-white p-3 rounded-lg hover:bg-blue-700 transition"
                                  title="Modifier le festival"
                                >
                                  <Edit size={20} />
                                </button>
                                <button
                                  onClick={() => navigate(`/manage-festivals/${event.id}/tournaments`)}
                                  className="bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 transition font-semibold"
                                  title="Gérer les tournois"
                                >
                                  Tournois
                                </button>
                                <button
                                  onClick={() => handleDelete(event)}
                                  disabled={deletingId === event.id}
                                  className="bg-red-600 text-white p-3 rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                                  title="Supprimer le festival"
                                >
                                  <Trash2 size={20} />
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Liste rapide des tournois */}
                          {subEvents.length > 0 && (
                            <div className="p-4 bg-gray-50">
                              <p className="text-sm font-semibold text-gray-700 mb-3">Aperçu des tournois :</p>
                              <div className="grid md:grid-cols-2 gap-2">
                                {subEvents.slice(0, 4).map(subEvent => (
                                  <div
                                    key={subEvent.id}
                                    className={`bg-white p-2 rounded border-l-4 ${
                                      subEvent.participating ? 'border-green-500' : 'border-gray-300'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-gray-800 truncate">
                                          {subEvent.title}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                          {new Date(subEvent.date).toLocaleDateString('fr-FR')} - {subEvent.time}
                                        </p>
                                      </div>
                                      {subEvent.participating && (
                                        <span className="text-green-500 ml-2">✓</span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              {subEvents.length > 4 && (
                                <button
                                  onClick={() => navigate(`/manage-festivals/${event.id}/tournaments`)}
                                  className="text-blue-600 text-sm font-semibold mt-2 hover:underline"
                                >
                                  Voir les {subEvents.length - 4} autres tournois →
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageFestivalsPage;
